import MixpanelTracker from "../utils/common/mixpanel-tracker.ts";
import VersionHelperUtil from "./version-helper-util.js";

class VersionHelper {
  static KEY_FW_VERSION = "fw_version";
  static KEY_UPDATE_INFO = "fw_update_info";
  static sheet = null;

  static async loadDialog() {
    //delayForCellEdit: wait till cell in workbook exits editing mode
    await Excel.run({ delayForCellEdit: true }, async (context) => {
      try {
        const currentVersion = await VersionHelperUtil.getDocumentProperties(context, VersionHelper.KEY_FW_VERSION);
        const metaData = await VersionHelperUtil.getMetaData();
        const newVersion = metaData.version;
        localStorage.setItem(VersionHelper.KEY_FW_VERSION, currentVersion)

        // pause all fetch calls, to stop functions to make API calls.
        window.fetchQueue.pauseQueue();

        // check for transformer files, return if no updates
        const versionList = VersionHelperUtil.getTransformerFileList(metaData.files, currentVersion);
        console.log(`Current Version: ${currentVersion} Latest Version: ${newVersion}`);

        let isUpdateAvailable = (!currentVersion || newVersion !== currentVersion) && versionList.length > 0;

        let sheets = context.workbook.worksheets;
        sheets.load("items,names");
        context.workbook.load("name");
        await context.sync();

        const transformersObject = await VersionHelperUtil.getTransformersObject(versionList);

        // check if it is a new workbook
        const formulasObject = await VersionHelper.getFWFormulasFromWorkBook(context, sheets, transformersObject);
        if (!formulasObject) {
          isUpdateAvailable = false;
          await VersionHelperUtil.setDocumentProperty(context, VersionHelper.KEY_FW_VERSION, newVersion);
          localStorage.setItem(VersionHelper.KEY_FW_VERSION, newVersion);
          return window.fetchQueue.startQueue();
        }

        if (!isUpdateAvailable) return window.fetchQueue.startQueue();

        // send event to sidebar (taskpane.html) to show update panel
        const customEvent = new CustomEvent("fw-update", { bubbles: true, detail: isUpdateAvailable });
        document.dispatchEvent(customEvent);

        // show update confirmation dialog
        const dialogUrl = window.location.origin + "/transformer/update.html";
        Office.context.ui.displayDialogAsync(dialogUrl, { height: 40, width: 35, displayInIframe: true }, async (result) => {
          const dialog = result.value;
          if (!dialog) return;

          await VersionHelperUtil.lockSheets(sheets, context);

          dialog.addEventHandler(Office.EventType.DialogEventReceived, (arg) => {
            // start default function execution if dialog is closed(12006)
            if (arg.error === 12006) window.fetchQueue.startQueue();
          });

          VersionHelper.handleMessageFromDialog(context, dialog, sheets, formulasObject, versionList, newVersion);
        });
      } catch (error) {
        // start default function executions
        window.fetchQueue.startQueue();
        console.error(error, error.stack);
        MixpanelTracker.trackEvent("Version update failed", { error, workbook: context.workbook.name });
      }
    });
  }

  static async getFWFormulasFromWorkBook(context, sheets, transformersObject) {
    const transformerFunctions = Object.keys(transformersObject)
      .map((key) => Object.getOwnPropertyNames(transformersObject[key]).filter((property) => !!property.match(/[A-Z]*/g)[0]))
      .flat(1);

    let formulasObject;

    for (let sheet of sheets.items) {
      const usedRange = sheet.getUsedRange();
      usedRange.load("formulas, columnIndex, rowIndex");
      await context.sync();

      const formulas = usedRange.formulas;
      let filteredFormulas = [];
      let debugFilteredFormulas = [];
      for (let relativeRowIndex = 0; relativeRowIndex < formulas.length; relativeRowIndex++) {
        for (let relativeColumnIndex = 0; relativeColumnIndex < formulas[relativeRowIndex].length; relativeColumnIndex++) {
          const formula = formulas[relativeRowIndex][relativeColumnIndex];
          if (!formula || typeof formula !== "string") continue;

          const cellColumnIndex = relativeColumnIndex + 1 + usedRange.columnIndex;
          const cellRowIndex = relativeRowIndex + 1 + usedRange.rowIndex;
          const cellColumnLetter = VersionHelperUtil.indexToLetter(cellColumnIndex);
          const cellAddress = cellColumnLetter + cellRowIndex;
          const cellRange = sheet.getRange(cellAddress);

          const fwFormula = VersionHelperUtil.getFormulaGroupsFromRegex(formula);
          if (!fwFormula || !transformerFunctions.includes(fwFormula.functionName)) continue;

          //keeps context of cellRange object between multiple context syncs.
          context.trackedObjects.add(cellRange);
          filteredFormulas.push({ fwFormula, cellRange });
          debugFilteredFormulas.push({ fwFormula, cellAddress });
        }
      }

      if (filteredFormulas.length > 0) {
        if (!formulasObject) formulasObject = {};
        formulasObject = { ...formulasObject, [sheet.name]: filteredFormulas };

        MixpanelTracker.trackEvent("Version upgrade identified functions", { debugFilteredFormulas, sheet: sheet.name });
      }
    }

    return formulasObject;
  }

  static handleMessageFromDialog(context, dialog, sheets, formulasObject, versionList, newVersion, currentVersion) {
    dialog.addEventHandler(Office.EventType.DialogMessageReceived, async (arg) => {
      const messageFromParent = JSON.parse(arg.message);

      switch (messageFromParent.eventType) {
        case "loaded":
          const updateDetails = await VersionHelperUtil.getUpdateDetails(versionList);
          VersionHelperUtil.messageDialogChild(dialog, VersionHelperUtil.EventType.UpdateDetails, { updateDetails });
          break;

        case "update":
          MixpanelTracker.trackEvent("Clicked on Update Version", { workbook: context.workbook.name, currentVersion });
          await VersionHelper.updateFunctions(context, dialog, sheets, formulasObject, versionList, newVersion, currentVersion);
          const filterRegex =
            /(^(https:\/\/([a-z0-9-]+[.])*(jcurve|fundwave|dealflow|investorportal|cloudfunctions|run))[.](app|net|cloud))|(^(https:\/\/[a-z0-9-]+[.](get)*fundwave)[.]com)/;
          window.fetchQueue.emptyQueue(filterRegex);
          break;

        case "cancel":
          MixpanelTracker.trackEvent("Canceled on Update Version", { workbook: context.workbook.name, currentVersion });
          window.fetchQueue.startQueue();
          dialog.close();
          break;

        case "done":
        default:
          MixpanelTracker.trackEvent("Version update successful", { workbook: context.workbook.name, currentVersion });
          window.fetchQueue.startQueue();
          dialog.close();

          VersionHelper.refreshUpdatedFunctions(context, sheets, formulasObject);
          break;
      }
    });
  }

  static async refreshUpdatedFunctions(context, sheets, formulasObject) {
    for (let sheet of sheets.items) {
      const sheetFormulas = formulasObject[sheet.name];

      if (!sheetFormulas) continue;

      for (const formulaDetail of sheetFormulas) {
        const range = formulaDetail.cellRange;
        range.calculate();
      }

      await context.sync();
    }
  }

  static async updateFunctions(context, dialog, sheets, formulasObject, versionList, newVersion, currentVersion) {
    const updateInfo = {};

    try {
      const updateInfoRaw = await VersionHelperUtil.getDocumentProperties(context, VersionHelper.KEY_UPDATE_INFO);

      updateInfo.raw = updateInfoRaw;

      try {
        if (updateInfoRaw) Object.assign(updateInfo, JSON.parse(updateInfoRaw));
      } catch {
        throw new Error("FAILED_TO_PARSE_UPDATE_INFO");
      }

      delete updateInfo.raw;

      for (const sheet of sheets.items) {
        const formulas = formulasObject[sheet.name] || [];
        sheet.protection.unprotect();

        if (formulas.length !== 0) {
          //start showing progress bar
          VersionHelperUtil.messageDialogChild(dialog, VersionHelperUtil.EventType.AddProgressBar, { label: sheet.name });

          await VersionHelper.updateFunctionsInBatch(context, dialog, sheet, formulas, versionList, updateInfo);
        }

        sheet.protection.unprotect();
      }

      await VersionHelperUtil.setDocumentProperty(context, VersionHelper.KEY_UPDATE_INFO, "");

      // send message to update.html
      VersionHelperUtil.messageDialogChild(dialog, VersionHelperUtil.EventType.UpdateComplete);

      // send event to taskpane.html to hide update dialog
      const customEvent = new CustomEvent("fw-update", { bubbles: true, detail: false });
      document.dispatchEvent(customEvent);

      // update excel document property
      await VersionHelperUtil.setDocumentProperty(context, VersionHelper.KEY_FW_VERSION, newVersion);
    } catch (error) {
      const data = this.sheet ? { label: this.sheet } : {};

      if (error.message === "FAILED_TO_PARSE_UPDATE_INFO") {
        data.label = "Failed to parse update info, resetting update info";
        MixpanelTracker.trackEvent("Failed to parse update info, resetting update info", { workbook: context.workbook.name, currentVersion, updateInfo });

        await VersionHelperUtil.setDocumentProperty(context, VersionHelper.KEY_UPDATE_INFO, "");
      }

      MixpanelTracker.trackEvent("Version update failed", { error, workbook: context.workbook.name, currentVersion, data });
      VersionHelperUtil.messageDialogChild(dialog, VersionHelperUtil.EventType.UpdateFailed, data);

      this.sheet = null;
      console.error(error);
    }
  }

  static async updateFunctionsInBatch(context, dialog, sheet, formulas, versionList, updateInfo) {
    try {
      sheet.load("protection/protected");
      await context.sync();

      if (sheet.protection.protected) sheet.protection.unprotect();

      const formulasCount = formulas.length * versionList.length;
      let progress = 0;
      for (const transformerVersion of versionList) {
        console.log("Applying transformer for: ", transformerVersion);
        const transformer = (await import(`./${transformerVersion}.js`)).Transformer;

        const sheetUpdateInfo = updateInfo?.[sheet.name];

        if (!!sheetUpdateInfo && sheetUpdateInfo.localeCompare(transformerVersion, undefined, { numeric: true, sensitivity: "base" }) !== -1) {
          console.log("Already updated for version, ", transformerVersion);

          progress += formulas.length;
          VersionHelperUtil.messageDialogChild(dialog, VersionHelperUtil.EventType.UpdateProgressBar, {
            label: sheet.name,
            progress: parseInt((progress / formulasCount) * 100),
          });

          continue;
        }

        for (const formula of formulas) {
          const fwFormula = formula.fwFormula;
          const cellRange = formula.cellRange;

          // update progress bar
          progress += 1;
          VersionHelperUtil.messageDialogChild(dialog, VersionHelperUtil.EventType.UpdateProgressBar, {
            label: sheet.name,
            progress: parseInt((progress / formulasCount) * 100),
          });

          if (!transformer[fwFormula.functionName]) continue;
          const paramList = VersionHelperUtil.convertStringParamsToList(fwFormula.params);
          const updatedFormula = transformer[fwFormula.functionName](fwFormula, paramList);
          cellRange.formulas = [[updatedFormula]];
        }

        // replaces all functions in a sheet.
        await context.sync();

        updateInfo[sheet.name] = transformerVersion;

        await VersionHelperUtil.setDocumentProperty(context, VersionHelper.KEY_UPDATE_INFO, JSON.stringify(updateInfo));
      }

      if (!sheet.protection.protected) sheet.protection.protect();
    } catch (error) {
      this.sheet = sheet.name;
      throw error;
    }
  }
}

document.addEventListener("fw-show-dialog", (e) => VersionHelper.loadDialog());

export default VersionHelper;
